import { Customization, EquipmentConfiguration } from '@/common/types/student';
import Customizations from '@/game/GameComponents/GameData/customizations.json';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import { getCharacterInventory } from '@/game/GameComponents/PhaserGame/GameInstance';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { getCustomizationFromId } from '@/game/GameComponents/PhaserGame/Utils/CosmeticUtils';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { ItemTabData } from '@/game/GameComponents/PhaserGame/Utils/ItemTabData';
import { GridLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/GridLayoutGroup';
import { ItemScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ItemScrollGroup';
import { ScrollGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/ScrollGroup';
import { getAnchorPlugin } from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import ShopGridItem from '@/game/prefabs/CosmeticShop/ShopGridItem';
import ShopTab from '@/game/prefabs/CosmeticShop/ShopTab';
import ShopTab_m from '@/game/prefabs/CosmeticShop/ShopTab_m';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import DefaultDenyButton from '@/game/prefabs/GeneralUI/Buttons/DefaultDenyButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import ComplexionSelection from '@/game/prefabs/GeneralUI/ComplexionSelection';
import PlayerDoll from '@/game/prefabs/GeneralUI/PlayerDoll';
import PlayerInventory from '@/game/scenes/PlayerInventory/PlayerInventory';

export class PlayerInventoryController {
  constructor(private playerInventory: PlayerInventory) {
    this.init();
  }

  inventoryGrid?: ScrollGroup;
  tabs: ShopTab[] = [];
  tabs_m: ShopTab_m[] = [];
  currentTab?: ShopTab | ShopTab_m;
  hasInitialized = false;
  awaitingClose = false;
  itemScrollGroup?: ItemScrollGroup;
  itemScrollGroup_m?: ItemScrollGroup;

  cosmeticOverrides: any;

  getResponsiveElements(key: keyof PlayerInventory) {
    return ResponsivenessManager.GetAllResponsiveComponentsFromScene(
      this.playerInventory,
      key,
    );
  }

  init() {
    if (this.hasInitialized) return;
    this.hasInitialized = true;

    const { playerInventory } = this;

    // Setup the complexion picker.
    playerInventory.complexionSelection.conjoinWithPicker(
      playerInventory.complexionSelection_m,
    );
    this.getResponsiveElements('complexionSelection').forEach(
      (complexionSelection: ComplexionSelection) => {
        complexionSelection.onComplexionSelected = (option: any) => {
          this.cosmeticOverrides.complexion = option.complexionId;
          this.getResponsiveElements('playerDoll').forEach(
            (playerDoll: PlayerDoll) => {
              playerDoll.setCosmeticOverrides(this.cosmeticOverrides);
            },
          );
        };
      },
    );

    // Clear item labels.
    this.getResponsiveElements('itemNameLabel').forEach(
      (itemNameLabel: Phaser.GameObjects.BitmapText) => {
        itemNameLabel.text = '';
      },
    );

    // Hide loading covers
    this.getResponsiveElements('mainLoadingCover').forEach(
      (mainLoadingCover: Phaser.GameObjects.Container) => {
        mainLoadingCover.setVisible(false);
      },
    );

    const characterInventory = getCharacterInventory();

    // Desktop item scroll group.
    this.itemScrollGroup = new ItemScrollGroup({
      scene: playerInventory,
      content: playerInventory.content,
      gridBacking: playerInventory.gridBacking,
      scrollBacking: playerInventory.scrollBarBacking,
      scrollHandle: playerInventory.scrollBarHandle,
      loadingContainer: playerInventory.loadingContainer,
      loadingSpinner: playerInventory.loadingSpinner,
      showItemPrices: false,
    });

    const initialFilter = (c: Customization) =>
      characterInventory.unlockedItems.includes(c.id) && c.slot === 'hats';

    this.itemScrollGroup.initialize((item: ShopGridItem) => {
      this.handleItemClicked(item);
      this.updateSelections();
    }, initialFilter);

    // Mobile item scroll group.
    this.itemScrollGroup_m = new ItemScrollGroup({
      scene: playerInventory,
      content: playerInventory.content_m,
      gridBacking: playerInventory.gridBacking_m,
      scrollBacking: playerInventory.scrollBarBacking_m,
      scrollHandle: playerInventory.scrollBarHandle_m,
      loadingContainer: playerInventory.loadingContainer_m,
      loadingSpinner: playerInventory.loadingSpinner_m,
      showItemPrices: false,
      gridLayoutConfig: {
        scene: playerInventory,
        elements: [],
        columns: 5,
        hPadding: 72,
        vPadding: 72,
        hMargin: 16,
        vMargin: 16,
        backingElement: playerInventory.gridBacking,
      },
    });

    this.itemScrollGroup_m.initialize((item: ShopGridItem) => {
      this.handleItemClicked(item);
      this.updateSelections();
    }, initialFilter);

    // Initialize Leave button.
    this.getResponsiveElements('leaveButton').forEach(
      (leaveButton: DefaultDenyButton) => {
        leaveButton.controller.setSubmissionCallback(
          this.handleEquipmentCancelled.bind(this),
        );
      },
    );

    // Initialize Close button.
    this.getResponsiveElements('closeButton').forEach(
      (closeButton: HUDButton) => {
        closeButton.submitSFX = 'panel-close';
        closeButton.setSubmissionCallback(
          this.handleEquipmentCancelled.bind(this),
        );
      },
    );

    // Initialize Save button.
    this.getResponsiveElements('saveButton').forEach(
      (saveButton: DefaultButton) => {
        saveButton.controller.setSubmissionCallback(
          this.handleEquipmentSaved.bind(this),
        );
      },
    );

    // Initialize Go To Shop button.
    this.getResponsiveElements('goToShopButton').forEach(
      (goToShopButton: DefaultButton) => {
        goToShopButton.controller.setSubmissionCallback(() => {
          this.close();
          EventBus.emit(GAME_EVENTS.COSMETIC_SHOP_OPENED, this);
        });
      },
    );

    // Set up the tab interactivity for desktop.
    const tabLayoutGroup = new GridLayoutGroup({
      scene: playerInventory,
      elements: [],
      columns: 1,
      hPadding: 0,
      vPadding: 60,
    });
    playerInventory.shopTabs.add(tabLayoutGroup);

    const tabData = ItemTabData;
    for (let t in tabData) {
      const newTab = new ShopTab(playerInventory, 0, 0);
      newTab.controller.onUserSelectedTab.push((tab: ShopTab | ShopTab_m) => {
        this.handleUserClickedTab(tab);
      });
      newTab.controller.setTabData(tabData[t]);
      playerInventory.add.existing(newTab);
      this.tabs?.push(newTab);

      tabLayoutGroup.addItem(newTab);
      this.itemScrollGroup?.updateInteractivityWindows();
    }

    // Setup the tab interactivity for mobile.
    const tabLayoutGroup_m = new GridLayoutGroup({
      scene: playerInventory,
      elements: [],
      columns: 8,
      hPadding: 50,
      vPadding: 0,
    });
    playerInventory.shopTabs_m.add(tabLayoutGroup_m);

    tabData.forEach((tabDatum, i) => {
      const newTab = new ShopTab_m(playerInventory, 0, 0);
      newTab.controller.onUserSelectedTab.push((tab: ShopTab | ShopTab_m) => {
        this.handleUserClickedTab(tab);
      });
      newTab.controller.setTabData(tabDatum);
      newTab.controller.joinToTab(this.tabs[i]);
      playerInventory.add.existing(newTab);
      this.tabs_m?.push(newTab);

      tabLayoutGroup_m.addItem(newTab);

      this.itemScrollGroup_m?.updateInteractivityWindows();
    });

    // Select the active tab.
    this.updateTabs();
    let firstActiveTab = this.tabs_m.find((tab) => tab.controller.isEnabled);
    if (firstActiveTab === undefined) firstActiveTab = this.tabs_m[0];
    this.selectTab(firstActiveTab);
    playerInventory.playerDoll.setCosmeticOverrides(this.cosmeticOverrides);

    // Setup equipment overrides.
    const equipmentConfig = getCharacterInventory().equippedItems;
    this.cosmeticOverrides = {
      ...equipmentConfig,
    };

    this.getResponsiveElements('complexionSelection').forEach(
      (complexionSelection: ComplexionSelection) => {
        complexionSelection.handleComplexionSelected(
          complexionSelection.complexionOptions.find(
            (option) => option.complexionId === equipmentConfig.complexion,
          ),
        );
      },
    );

    this.getResponsiveElements('noItemsContainer').forEach(
      (noItemsContainer: Phaser.GameObjects.Container) => {
        noItemsContainer.setVisible(false);
      },
    );

    this.initializeResponsiveness();

    // Event listeners.
    EventsManager.registerEventOn(
      playerInventory,
      GAME_EVENTS.PLAYER_PURCHASED_COSMETIC,
      this.handleCosmeticPurchased,
      this,
    );
    EventsManager.registerEventOn(
      playerInventory,
      GAME_EVENTS.PLAYER_TEXTURES_FINISHED_REFRESH,
      this.handlePlayerRefreshFinished,
      this,
    );
  }

  refresh() {
    if (!this.itemScrollGroup) return;
    const characterInventory = getCharacterInventory();
    const filter = (c: Customization) =>
      characterInventory.unlockedItems.find((i: number) => i === c.id) !==
        undefined && c.slot === this.currentTab?.controller.tabData?.slot;

    this.itemScrollGroup.setItemFilter(
      filter,
      this.handleFilterUpdated.bind(this),
    );

    this.itemScrollGroup_m?.setItemFilter(
      filter,
      this.handleFilterUpdated.bind(this),
    );

    const customizations = Customizations as Customization[];
    this.getResponsiveElements('noItemsContainer').forEach(
      (noItemsContainer: Phaser.GameObjects.Container) => {
        noItemsContainer.setVisible(customizations.filter(filter).length === 0);
      },
    );

    this.updateNotifications();
    this.updateTabs();
  }

  updateNotifications() {
    if (!this.itemScrollGroup) return;
    const newItems = getCharacterInventory().newItems;

    // Show notifications on new items.
    this.itemScrollGroup.items.forEach((item) => {
      item.setNotificationVisible(
        !!newItems.find((i: number) => i === item.cosmetic?.id),
      );
    });

    // Set tab notifications.
    this.tabs.forEach((tab: ShopTab) => {
      tab.controller.setNotificationActive(false);
      newItems.forEach((item: number) => {
        const customization = getCustomizationFromId(item);
        if (customization?.slot === tab.controller.tabData?.slot)
          tab.controller.setNotificationActive(true);
      });
    });
  }

  updateTabs() {
    // Disable tabs that have no cosmetics.
    // this.tabs.forEach((tab) => {
    //   tab.setDisabled(
    //     !hasUnlockedACustomizationInSlot(tab.tabData?.slot || ''),
    //   );
    // });
  }

  getCurrentLoadout() {
    return {
      ...this.cosmeticOverrides,
    };
  }

  updateSelections() {
    const equippedItems = this.getCurrentLoadout();
    // Set selected item to be highlighted.
    const items = [
      ...(this.itemScrollGroup?.items || []),
      ...(this.itemScrollGroup_m?.items || []),
    ];
    items.forEach((item) => {
      item.setIsSelected(false);
      if (!item.cosmetic) return;
      const itemSlot = item.cosmetic?.slot;
      if (Object.values(equippedItems).includes(item.cosmetic!.id)) {
        item.setIsSelected(true);
      } else if (equippedItems[itemSlot] === undefined) {
        // Fallback to what the player is actually wearing.
        const equippedItems = getCharacterInventory().equippedItems;
        item.setIsSelected(
          Object.values(equippedItems).find((e) => e === item.cosmetic?.id),
        );
      }
    });
  }

  selectTab(tab: ShopTab | ShopTab_m) {
    this.getAllTabs()?.forEach((t: ShopTab | ShopTab_m) => {
      t.controller.setSelected(false);
    });
    tab.controller.setSelected(true);
    tab.controller.jointTab?.controller.setSelected(true);
    this.currentTab = tab;
  }

  getAllTabs() {
    return [...this.tabs, ...this.tabs_m];
  }

  initializeResponsiveness() {
    const { playerInventory } = this;
    // Breakpoints.
    ResponsivenessManager.InitializeResponsiveBreakpoints({
      scene: playerInventory,
      desktopView: playerInventory.desktopView,
      mobileView: playerInventory.mobileView,
    });

    // Responsiveness.
    const anchor = getAnchorPlugin(playerInventory);
    anchor.add(playerInventory.mainContainer, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(playerInventory.mainContainer_m, {
      centerX: '50%',
      centerY: '50%',
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      playerInventory.mainContainer,
    );
  }

  handleItemClicked(item: ShopGridItem) {
    if (!item.cosmetic) return;
    this.getResponsiveElements('itemNameLabel').forEach(
      (itemNameLabel: Phaser.GameObjects.BitmapText) => {
        itemNameLabel.text = item.cosmetic?.name || '';
      },
    );

    item.setIsSelected(true);

    const equippedItems = this.getCurrentLoadout();
    const isEquipped = Object.values(equippedItems).includes(item.cosmetic!.id);
    if (isEquipped) {
      // Unequip it.
      this.cosmeticOverrides[
        item.cosmetic.slot as keyof EquipmentConfiguration
      ] = null;
    } else {
      // Equip it.
      this.cosmeticOverrides[
        item.cosmetic.slot as keyof EquipmentConfiguration
      ] = item.cosmetic.id;
      EventBus.emit(GAME_EVENTS.PLAYER_PREVIEWED_COSMETIC, item.cosmetic);
    }
    this.getResponsiveElements('playerDoll').forEach(
      (playerDoll: PlayerDoll) => {
        playerDoll.setCosmeticOverrides(this.cosmeticOverrides);
      },
    );

    this.updateNotifications();
    this.updateSelections();
    this.updateTabs();
  }

  handleUserClickedTab(tab: ShopTab | ShopTab_m) {
    this.selectTab(tab);
    this.refresh();
  }

  handleCosmeticPurchased(_cosmetic: Customization) {
    if (!this.hasInitialized) return;
    this.refresh();
  }

  handleFilterUpdated() {
    this.updateSelections();
    this.updateNotifications();
  }

  handleEquipmentSaved() {
    // Equip all the cosmetic overrides.
    Object.keys(this.cosmeticOverrides).forEach((key: any) => {
      const val = this.cosmeticOverrides[key];
      if (val !== null && typeof val === 'number') {
        EventBus.emit(
          GAME_EVENTS.PLAYER_EQUIPPED_COSMETIC,
          getCustomizationFromId(val),
        );
      } else if (val === null) {
        const equippedItem =
          getCharacterInventory().equippedItems[
            key as keyof EquipmentConfiguration
          ];
        if (equippedItem !== null) {
          EventBus.emit(
            GAME_EVENTS.PLAYER_UNEQUIPPED_COSMETIC,
            getCustomizationFromId(equippedItem),
          );
        }
      }
    });
    // Will close once the player has finished refreshing.
    this.awaitingClose = true;
    this.close();
  }

  handleEquipmentCancelled() {
    // Reset overrides.
    const { playerInventory } = this;
    this.cosmeticOverrides = {};
    playerInventory.playerDoll.setCosmeticOverrides({});
    this.updateSelections();
    playerInventory.complexionSelection.resetComplexionSelection();
    this.close();
  }

  handlePlayerRefreshFinished() {
    const { playerInventory } = this;
    this.awaitingClose = false;
    playerInventory.mainLoadingCover.setVisible(false);
    this.close();
  }

  handleComplexionSelected(option: any) {
    const { playerInventory } = this;
    this.cosmeticOverrides.complexion = option.complexionId;
    EventBus.emit(GAME_EVENTS.PLAYER_PREVIEWED_COSMETIC, option.complexionId);
    playerInventory.playerDoll.setCosmeticOverrides(this.cosmeticOverrides);

    playerInventory.complexionSelection.complexionOptions.forEach((o) => {
      playerInventory.complexionSelection.setComplexionEquipped(
        o,
        o === option,
      );
    });
  }

  close() {
    getSceneManager()?.pauseScene('PlayerInventory');
    EventBus.emit(GAME_EVENTS.INVENTORY_CLOSED);
  }
}
